import React, { useState, useMemo, useEffect, useCallback } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Close,
  ArrowForward as ArrowIcon,
  ExpandMore,
  OpenInNew as LoadIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import {
  useLazyGetClusterAuditQuery,
  useDeleteDiscrepancyMutation,
  useUpdateClassificationDiscrepancyValuesMutation,
  useUpdateDiscrepancyReviewStatusMutation,
  useAddDiscrepancyCommentMutation,
  useRemoveDiscrepancyCommentMutation,
} from "@/services/customs-document/customs-document-api";
import { useCalculateDutiesQuery } from "@/services/product-catalogue";
import { useAppSelector } from "@/store";
import { roles } from "@/roles";
import { useSnackbar } from "@/hooks";
import { HTSCodeWithTooltip } from "@/components/hts-code-with-tooltip";
import { formatCurrencyPrecise, getSeverityConfig, classificationItemToReviewRecord } from "./verification-helpers";
import { formatHTSCode } from "@/utils";
import SeverityDot from "@/components/severity-dot";
import DiscrepancyReviewControls, {
  type ReviewComment,
  type ReviewRecord,
  type ReviewStatus,
} from "./discrepancy-review-controls";
import { tokens, labelStyle } from "@/tokens";
import { tableContainerSx, tableCompactSx } from "@/components/table/table-styles";

/* ── Shared card style constants ── */
const sectionSx = { px: 2.5, pb: 1.5 } as const;

interface ClassificationDetailDialogProps {
  open: boolean;
  onClose: () => void;
  item: ClassificationDiscrepancyItem | null;
  onEditSuccess?: () => void;
  onDeleteSuccess?: () => void;
  onExited?: () => void;
}

const getSeverityColor = (severity: string) => {
  switch (severity) {
    case "HIGH": return tokens.status.danger;
    case "MEDIUM": return tokens.status.warning;
    case "LOW": return tokens.status.info;
    default: return tokens.text.secondary;
  }
};

const getCategoryLabel = (category: string) => {
  switch (category) {
    case "VALUE_MISMATCH": return "Value Mismatch";
    case "QUANTITY_MISMATCH": return "Quantity Mismatch";
    case "HTS_CLASSIFICATION": return "HTS Classification";
    case "COUNTRY_OF_ORIGIN": return "Country of Origin";
    case "MANUFACTURER_MISMATCH": return "Manufacturer Mismatch";
    case "MISSING_DOCUMENT": return "Missing Document";
    case "DATE_DISCREPANCY": return "Date Discrepancy";
    case "IDENTIFIER_MISMATCH": return "Identifier Mismatch";
    case "DUTY_CALCULATION": return "Duty Calculation";
    default: return category;
  }
};


const ClassificationDetailDialog: React.FC<ClassificationDetailDialogProps> = ({
  open,
  onClose,
  item,
  onEditSuccess,
  onDeleteSuccess,
  onExited,
}) => {
  const { user } = useAppSelector((state) => state.auth);
  const { selectedTenant } = useAppSelector((state) => state.tenantManagement);
  const isTDAdmin = user?.userRole === roles.TDADMIN;
  const isSpOwner = user?.userRole === roles.SPOWNER;
  const tenantId = isTDAdmin && selectedTenant ? selectedTenant._id : undefined;
  const { show: showSnackbar } = useSnackbar();

  const canRemoveComment = useCallback((comment: ReviewComment) => {
    if (isTDAdmin || isSpOwner) return true;
    return !!user?._id && comment.authorId === user._id;
  }, [isTDAdmin, isSpOwner, user?._id]);

  const [getClusterAudit] = useLazyGetClusterAuditQuery();
  const [deleteDiscrepancy, { isLoading: isDeleting }] = useDeleteDiscrepancyMutation();
  const [updateClassificationValues, { isLoading: isUpdatingValues }] = useUpdateClassificationDiscrepancyValuesMutation();
  const [updateReviewStatus, { isLoading: isStatusUpdating }] = useUpdateDiscrepancyReviewStatusMutation();
  const [addComment, { isLoading: isAddingComment }] = useAddDiscrepancyCommentMutation();
  const [removeComment] = useRemoveDiscrepancyCommentMutation();

  const [fullAudit, setFullAudit] = useState<ClusterAudit | null>(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [metalPercentage, setMetalPercentage] = useState<number | undefined>(undefined);

  // Local review state — updated immediately from mutation responses
  const [localReview, setLocalReview] = useState<ReviewRecord | null>(null);

  useEffect(() => {
    setLocalReview(item ? classificationItemToReviewRecord(item) : null);
  }, [item]);

  useEffect(() => {
    if (item?.metalPercentage != null) {
      setMetalPercentage(Math.round(item.metalPercentage * 100));
    } else {
      setMetalPercentage(undefined);
    }
  }, [item]);

  const isReviewUpdating = isStatusUpdating || isAddingComment;

  // Manual edit state
  const [isEditMode, setIsEditMode] = useState(false);
  const [editFiledHtsCode, setEditFiledHtsCode] = useState("");
  const [editCorrectHtsCode, setEditCorrectHtsCode] = useState("");
  const [editFiledDutyRate, setEditFiledDutyRate] = useState("");
  const [editCorrectDutyRate, setEditCorrectDutyRate] = useState("");
  const [editLineNumber, setEditLineNumber] = useState("");

  // Format effective date as yyyy-MM-dd for duty calculator
  const formatEffectiveDate = (dateStr?: string): string | undefined => {
    if (!dateStr) return undefined;
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return undefined;
      return d.toISOString().split("T")[0];
    } catch {
      return undefined;
    }
  };

  // Duty calculator for filed HTS
  const filedDutyInput = useMemo(() => {
    if (!item?.filedHtsCode || !item?.countryOfOriginCode) return null;
    return {
      htsCode: item.filedHtsCode,
      countryOfOrigin: item.countryOfOriginCode,
      effectiveDate: formatEffectiveDate(item.entryDate),
      metalPercentage,
    };
  }, [item?.filedHtsCode, item?.countryOfOriginCode, item?.entryDate, metalPercentage]);

  // Duty calculator for correct HTS
  // For WRONG_METAL_SPLIT: use the correct metal percentage from document evidence
  const correctDutyInput = useMemo(() => {
    if (!item?.correctHtsCode || !item?.countryOfOriginCode) return null;
    const correctMetalPct = item.classificationSubType === "WRONG_METAL_SPLIT" && item.correctMetalPercentage != null
      ? Math.round(item.correctMetalPercentage * 100)
      : metalPercentage;
    return {
      htsCode: item.correctHtsCode,
      countryOfOrigin: item.countryOfOriginCode,
      effectiveDate: formatEffectiveDate(item.entryDate),
      metalPercentage: correctMetalPct,
    };
  }, [item?.correctHtsCode, item?.countryOfOriginCode, item?.entryDate, item?.classificationSubType, item?.correctMetalPercentage, metalPercentage]);

  const { data: filedDutyCalc, isFetching: filedDutyFetching } = useCalculateDutiesQuery(
    filedDutyInput as any,
    { skip: !filedDutyInput }
  );
  const { data: correctDutyCalc, isFetching: correctDutyFetching } = useCalculateDutiesQuery(
    correctDutyInput as any,
    { skip: !correctDutyInput }
  );

  const handleClose = () => {
    setFullAudit(null);
    setAuditError(null);
    setShowDeleteConfirm(false);
    setMetalPercentage(undefined);
    setIsEditMode(false);
    setEditFiledHtsCode("");
    setEditCorrectHtsCode("");
    setEditFiledDutyRate("");
    setEditCorrectDutyRate("");
    setEditLineNumber("");
    onClose();
  };

  const handleStartEdit = () => {
    if (!item) return;
    setEditFiledHtsCode(item.filedHtsCode || "");
    setEditCorrectHtsCode(item.correctHtsCode || "");
    setEditFiledDutyRate(filedDutyCalc ? (filedDutyCalc.totalDutyRate * 100).toFixed(2) : "");
    setEditCorrectDutyRate(correctDutyCalc ? (correctDutyCalc.totalDutyRate * 100).toFixed(2) : "");
    const lineNum = item.lineNumber != null
      ? item.lineNumber.toString()
      : item.entryLine
        ? item.entryLine.split('-').pop() || ""
        : "";
    setEditLineNumber(lineNum);
    setIsEditMode(true);
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditFiledHtsCode("");
    setEditCorrectHtsCode("");
    setEditFiledDutyRate("");
    setEditCorrectDutyRate("");
    setEditLineNumber("");
  };

  const handleSaveClassificationValues = async () => {
    if (!item) return;

    if (!editFiledHtsCode.trim() || !editCorrectHtsCode.trim()) {
      showSnackbar("Error", "Please enter both HTS codes", "error");
      return;
    }

    const filedRate = parseFloat(editFiledDutyRate);
    const correctRate = parseFloat(editCorrectDutyRate);

    if (isNaN(filedRate) || isNaN(correctRate)) {
      showSnackbar("Error", "Please enter valid duty rates", "error");
      return;
    }

    const lineNumber = editLineNumber.trim() ? parseInt(editLineNumber, 10) : undefined;
    if (editLineNumber.trim() && (isNaN(lineNumber!) || lineNumber! < 1)) {
      showSnackbar("Error", "Please enter a valid line number", "error");
      return;
    }

    try {
      const result = await updateClassificationValues({
        auditId: item.auditId,
        discrepancyId: item.discrepancyId,
        filedHtsCode: editFiledHtsCode.trim(),
        correctHtsCode: editCorrectHtsCode.trim(),
        filedDutyRate: filedRate / 100,
        correctDutyRate: correctRate / 100,
        lineNumber,
        tenantId,
      }).unwrap();

      if (result.success) {
        showSnackbar("Success", "Classification values updated", "success");
        setIsEditMode(false);
        onEditSuccess?.();
        handleClose();
      } else {
        showSnackbar("Error", result.error || "Failed to update values", "error");
      }
    } catch (error) {
      showSnackbar("Error", "Failed to update classification values", "error");
    }
  };

  const handleDelete = async () => {
    if (!item) return;
    try {
      const result = await deleteDiscrepancy({
        auditId: item.auditId,
        discrepancyId: item.discrepancyId,
        tenantId,
      }).unwrap();

      if (result.success) {
        showSnackbar("Success", "Discrepancy deleted", "success");
        onDeleteSuccess?.();
        handleClose();
      } else {
        showSnackbar("Error", result.error || "Failed to delete discrepancy", "error");
      }
    } catch (error) {
      showSnackbar("Error", "Failed to delete discrepancy", "error");
    }
  };

  const handleLoadAudit = async () => {
    if (!item) return;
    setAuditLoading(true);
    setAuditError(null);
    try {
      const result = await getClusterAudit({
        clusterId: item.clusterId,
        tenantId,
      }).unwrap();
      if (result) {
        setFullAudit(result);
      } else {
        setAuditError("No audit data found.");
      }
    } catch {
      setAuditError("Failed to load audit details.");
    } finally {
      setAuditLoading(false);
    }
  };

  const applyReviewResponse = useCallback((resp: DiscrepancyReviewResponse) => {
    setLocalReview({
      status: (resp.reviewStatus as ReviewStatus) || null,
      statusSetBy: resp.reviewStatusSetBy || null,
      statusSetAt: resp.reviewStatusSetAt || null,
      comments: (resp.reviewComments || []).map((c) => ({
        id: c.id,
        text: c.text,
        authorId: c.authorId,
        authorName: c.authorName,
        createdAt: c.createdAt,
      })),
    });
  }, []);

  const handleStatusChange = useCallback(async (status: ReviewStatus) => {
    if (!item) return;
    try {
      const resp = await updateReviewStatus({
        auditId: item.auditId,
        discrepancyId: item.discrepancyId,
        status,
        tenantId,
      }).unwrap();
      applyReviewResponse(resp);
    } catch {
      showSnackbar("Error", "Failed to update status", "error");
    }
  }, [item, updateReviewStatus, tenantId, applyReviewResponse, showSnackbar]);

  const handleAddComment = useCallback(async (text: string) => {
    if (!item) return;
    try {
      const resp = await addComment({
        auditId: item.auditId,
        discrepancyId: item.discrepancyId,
        text,
        tenantId,
      }).unwrap();
      applyReviewResponse(resp);
    } catch {
      showSnackbar("Error", "Failed to add comment", "error");
    }
  }, [item, addComment, tenantId, applyReviewResponse, showSnackbar]);

  const handleRemoveComment = useCallback(async (commentId: string) => {
    if (!item) return;
    try {
      const resp = await removeComment({
        auditId: item.auditId,
        discrepancyId: item.discrepancyId,
        commentId,
        tenantId,
      }).unwrap();
      applyReviewResponse(resp);
    } catch {
      showSnackbar("Error", "Failed to remove comment", "error");
    }
  }, [item, removeComment, tenantId, applyReviewResponse, showSnackbar]);

  const severityConfig = item ? getSeverityConfig(item.severity) : null;

  // Ch.99 codes filed on the affected line(s) — used to determine which tariffs actually apply
  const affectedLineCh99Codes = useMemo(() => {
    if (!item?.entryLineData) return null;
    // Find lines that are actually affected by this discrepancy
    const affected = item.entryLineData.filter(
      (line) =>
        item.lineNumbers?.includes(line.lineNumber) ||
        (item.entryLines || []).some((el) => el.toLowerCase() === (line.entryLine || "").toLowerCase())
    );
    // Fallback to scalar lineNumber
    if (affected.length === 0 && item.lineNumber != null) {
      const fallback = item.entryLineData.find((l) => l.lineNumber === item.lineNumber);
      if (fallback) affected.push(fallback);
    }
    if (affected.length === 0) return null;
    const codes = new Set<string>();
    let hasSec232 = false;
    for (const line of affected) {
      for (const code of (line.chapter99Codes || [])) {
        codes.add(code.replace(/\./g, ""));
      }
      if (line.hasSec232) hasSec232 = true;
    }
    return { codes, hasSec232 };
  }, [item?.entryLineData, item?.lineNumbers, item?.entryLines, item?.lineNumber]);

  // Detect if the affected line is part of a metal split (same HTS, one line has 232, the other doesn't)
  const metalSplitContext = useMemo(() => {
    if (!item?.entryLineData || !item?.lineNumbers) return null;
    const affectedLines = item.entryLineData.filter((l) => item.lineNumbers!.includes(l.lineNumber));
    if (affectedLines.length === 0) return null;
    const affected = affectedLines[0];
    // Look for a companion line with the same HTS that forms the other half of the split
    // (one has 232, the other doesn't)
    const companion = item.entryLineData.find(
      (l) => !item.lineNumbers!.includes(l.lineNumber) && l.htsCode === affected.htsCode && l.hasSec232 !== affected.hasSec232
    );
    if (!companion) return null;
    return {
      isNonMetal: !affected.hasSec232,
      isMetal: !!affected.hasSec232,
      companionLineNumber: companion.lineNumber,
    };
  }, [item?.entryLineData, item?.lineNumbers]);

  // Check if a tariff line from the breakdown is actually filed on the affected line(s)
  const isTariffApplicable = useCallback((columnName: string): boolean => {
    if (!affectedLineCh99Codes) return true;
    // Extract Ch.99 code pattern from column name (e.g., "9903.85.08" or "9903.8803")
    const match = columnName.match(/\b(99\d{2}[\.\s]?\d{2}[\.\s]?\d{2,4})\b/);
    if (!match) return true; // Not a Ch.99 tariff (e.g., General Duty) — always applies
    const normalized = match[1].replace(/[\.\s]/g, "");
    return affectedLineCh99Codes.codes.has(normalized);
  }, [affectedLineCh99Codes]);

  // Actual Ch.99 codes filed on affected lines — derived from entry line data (source of truth)
  // instead of AI's filedCh99Codes which can miss codes
  const actualFiledCh99Codes = useMemo(() => {
    if (!affectedLineCh99Codes) return null;
    if (!item?.entryLineData) return null;
    const affected = item.entryLineData.filter(
      (line) =>
        item.lineNumbers?.includes(line.lineNumber) ||
        (item.entryLines || []).some((el) => el.toLowerCase() === (line.entryLine || "").toLowerCase())
    );
    if (affected.length === 0 && item.lineNumber != null) {
      const fallback = item.entryLineData.find((l) => l.lineNumber === item.lineNumber);
      if (fallback) affected.push(fallback);
    }
    // Collect codes preserving original dotted format for display
    const codes: string[] = [];
    const seen = new Set<string>();
    for (const line of affected) {
      for (const code of (line.chapter99Codes || [])) {
        const normalized = code.replace(/\./g, "");
        if (!seen.has(normalized)) {
          seen.add(normalized);
          codes.push(code);
        }
      }
    }
    return codes;
  }, [item?.entryLineData, item?.lineNumbers, item?.entryLines, item?.lineNumber, affectedLineCh99Codes]);

  // Actual effective rate paid from ACE data
  const actualPaidRate = useMemo(() => {
    if (item?.paidDuties?.totalDuty != null && item?.declaredValue && item.declaredValue > 0) {
      return item.paidDuties.totalDuty / item.declaredValue;
    }
    return null;
  }, [item?.paidDuties?.totalDuty, item?.declaredValue]);

  // Compute duty impact for metal split: (correctMetal% - filedMetal%) × sec232Rate × combinedValue
  const metalSplitDutyImpact = useMemo(() => {
    if (item?.classificationSubType !== "WRONG_METAL_SPLIT") return null;
    if (item.filedMetalPercentage == null || item.correctMetalPercentage == null) return null;
    if (!item.declaredValue || item.declaredValue <= 0) return null;
    const dutyCalc = filedDutyCalc || correctDutyCalc;
    if (!dutyCalc) return null;
    // Find Section 232 rate from the additional tariffs
    const sec232Rate = (dutyCalc as any).additionalTariffs
      ?.filter((t: any) => /\b232\b/.test(t.columnName || ""))
      .reduce((sum: number, t: any) => sum + (t.rate || 0), 0) ?? 0;
    if (sec232Rate === 0) return null;
    const metalPctDelta = item.correctMetalPercentage - item.filedMetalPercentage;
    const rateChange = metalPctDelta * sec232Rate;
    const dutyImpact = rateChange * item.declaredValue;
    return { rateChange, dutyImpact, sec232Rate, isUnderpayment: dutyImpact > 0 };
  }, [item?.classificationSubType, item?.filedMetalPercentage, item?.correctMetalPercentage, item?.declaredValue, filedDutyCalc, correctDutyCalc]);

  // Metal split: find affected lines and compute expected values at correct metal %
  const metalSplitAnalysis = useMemo(() => {
    if (item?.classificationSubType !== "WRONG_METAL_SPLIT") return null;
    if (!item.entryLineData || item.entryLineData.length < 2) return null;
    if (item.filedMetalPercentage == null || item.correctMetalPercentage == null) return null;
    if (!item.declaredValue || item.declaredValue <= 0) return null;

    // Find AFFECTED lines specifically — not just any matching HTS
    const affectedHts = item.filedHtsCode?.replace(/\./g, "");
    const matchesHts = (code?: string) => !affectedHts || (code?.replace(/\./g, "") === affectedHts);
    const isAffected = (line: typeof item.entryLineData[0]) =>
      item.lineNumbers?.includes(line.lineNumber) ||
      (item.entryLines || []).some((el) => el.toLowerCase() === (line.entryLine || "").toLowerCase());

    const affected = item.entryLineData.filter((l) => isAffected(l) && matchesHts(l.htsCode));
    // Fallback: if no affected lines found, use first matching pair
    const metalLine = (affected.length >= 2 ? affected : item.entryLineData).find((l) => l.hasSec232 && matchesHts(l.htsCode));
    const nonMetalLine = (affected.length >= 2 ? affected : item.entryLineData).find((l) => !l.hasSec232 && matchesHts(l.htsCode));

    if (!metalLine || !nonMetalLine) return null;
    if (!metalLine.goodsValue || metalLine.goodsValue <= 0) return null;
    if (!nonMetalLine.goodsValue || nonMetalLine.goodsValue <= 0) return null;

    const combinedValue = item.declaredValue;
    const aceMetalDuty = metalLine.lineDutyAmount ?? 0;
    const aceNonMetalDuty = nonMetalLine.lineDutyAmount ?? 0;
    const aceTotal = aceMetalDuty + aceNonMetalDuty;

    // Expected goods values at correct metal %
    const expectedMetalValue = item.correctMetalPercentage * combinedValue;
    const expectedNonMetalValue = (1 - item.correctMetalPercentage) * combinedValue;

    // Derive ACE effective rates per line (from actual entry data)
    const metalRate = aceMetalDuty / metalLine.goodsValue;
    const nonMetalRate = aceNonMetalDuty / nonMetalLine.goodsValue;

    // Expected duty = expected value × rate (rates stay same, value changes)
    const expectedMetalDuty = expectedMetalValue * metalRate;
    const expectedNonMetalDuty = expectedNonMetalValue * nonMetalRate;
    const expectedTotal = expectedMetalDuty + expectedNonMetalDuty;

    return {
      metalRate,
      nonMetalRate,
      metalLine,
      nonMetalLine,
      filed: {
        metalPct: item.filedMetalPercentage,
        metalValue: metalLine.goodsValue,
        nonMetalValue: nonMetalLine.goodsValue,
        metalDuty: aceMetalDuty,
        nonMetalDuty: aceNonMetalDuty,
        total: aceTotal,
      },
      expected: {
        metalPct: item.correctMetalPercentage,
        metalValue: expectedMetalValue,
        nonMetalValue: expectedNonMetalValue,
        metalDuty: expectedMetalDuty,
        nonMetalDuty: expectedNonMetalDuty,
        total: expectedTotal,
      },
      difference: aceTotal - expectedTotal,
    };
  }, [item]);

  // Style tokens matching tariff analysis drawer
  const hdr = { ...labelStyle, whiteSpace: "nowrap" } as const;
  const val = { fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.medium, color: tokens.text.body } as const;
  const numberFieldSx = { "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": { WebkitAppearance: "none", margin: 0 }, "& input[type=number]": { MozAppearance: "textfield" } } as const;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      SlideProps={{ onExited: () => onExited?.() }}
    >
      {item && severityConfig ? (
      <Box sx={{ width: { xs: "100vw", sm: 820 }, maxWidth: "100vw", height: "100%", display: "flex", flexDirection: "column", bgcolor: tokens.bg.surface }}>

        {/* Header */}
        <Box sx={{ px: 2, py: 0.75, display: "flex", alignItems: "center", justifyContent: "space-between", bgcolor: tokens.bg.hover, borderBottom: `1px solid ${tokens.border.default}` }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.bold, color: tokens.text.primary }}>
              Classification Discrepancy
            </Typography>
            <SeverityDot severity={item.severity?.toLowerCase() as "high" | "medium" | "low"} />
            {(() => {
              const subType = item.classificationSubType;
              const chipConfig = subType === "WRONG_METAL_SPLIT"
                ? { label: "Metal Split", bgcolor: "#FEF3C7", color: "#92400E", border: "#FDE68A" }
                : subType === "WRONG_STACKING"
                ? { label: "Stacking", bgcolor: "#EDE9FE", color: "#5B21B6", border: "#DDD6FE" }
                : { label: "HTS Classification", bgcolor: "#E0E7FF", color: "#4F46E5", border: "#C7D2FE" };
              return (
                <Chip
                  label={chipConfig.label}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: tokens.fontSize["2xs"],
                    fontWeight: tokens.fontWeight.bold,
                    borderRadius: 0.5,
                    bgcolor: chipConfig.bgcolor,
                    color: chipConfig.color,
                    border: `1px solid ${chipConfig.border}`,
                  }}
                />
              );
            })()}
            {metalSplitContext && (
              <Chip
                label={metalSplitContext.isNonMetal ? `Non-Metal (split w/ Line ${metalSplitContext.companionLineNumber})` : `Metal (split w/ Line ${metalSplitContext.companionLineNumber})`}
                size="small"
                sx={{
                  height: 20,
                  fontSize: tokens.fontSize["2xs"],
                  fontWeight: tokens.fontWeight.bold,
                  borderRadius: 0.5,
                  bgcolor: metalSplitContext.isNonMetal ? "#F0F9FF" : "#FEF3C7",
                  color: metalSplitContext.isNonMetal ? "#0369A1" : "#92400E",
                  border: `1px solid ${metalSplitContext.isNonMetal ? "#BAE6FD" : "#FDE68A"}`,
                }}
              />
            )}
          </Stack>
          <IconButton onClick={handleClose} size="small" sx={{ color: tokens.text.muted }}>
            <Close sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        {/* ── Action Toolbar ── */}
        <Box sx={{ px: 2, py: 0.5, display: "flex", justifyContent: "flex-end", alignItems: "center", borderBottom: `1px solid ${tokens.border.light}`, minHeight: 36 }}>
          {isEditMode ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <Button size="small" onClick={handleCancelEdit} disabled={isUpdatingValues} sx={{ fontSize: tokens.fontSize.sm2, color: tokens.text.secondary, textTransform: "none" }}>Cancel</Button>
              <Button size="small" variant="contained" onClick={handleSaveClassificationValues} disabled={isUpdatingValues} sx={{ fontSize: tokens.fontSize.sm2, textTransform: "none", bgcolor: tokens.brand.primary, "&:hover": { bgcolor: tokens.brand.primaryHover }, fontWeight: tokens.fontWeight.bold, borderRadius: tokens.radius.md, minWidth: 60 }}>
                {isUpdatingValues ? <CircularProgress size={16} color="inherit" /> : "Save"}
              </Button>
            </Stack>
          ) : showDeleteConfirm ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography sx={{ fontSize: tokens.fontSize.sm2, color: tokens.status.danger, fontWeight: tokens.fontWeight.bold }}>Delete permanently?</Typography>
              <Button size="small" onClick={() => setShowDeleteConfirm(false)} disabled={isDeleting} sx={{ fontSize: tokens.fontSize.sm2, color: tokens.text.secondary, textTransform: "none" }}>Cancel</Button>
              <Button size="small" variant="contained" onClick={handleDelete} disabled={isDeleting} sx={{ fontSize: tokens.fontSize.sm2, textTransform: "none", bgcolor: tokens.status.danger, "&:hover": { bgcolor: tokens.status.dangerHover }, fontWeight: tokens.fontWeight.bold, borderRadius: tokens.radius.md, minWidth: 60 }}>
                {isDeleting ? <CircularProgress size={16} color="inherit" /> : "Delete"}
              </Button>
            </Stack>
          ) : (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Button
                size="small"
                startIcon={<EditIcon sx={{ fontSize: 15 }} />}
                onClick={handleStartEdit}
                sx={{ fontSize: tokens.fontSize.sm2, textTransform: "none", color: tokens.text.body, fontWeight: tokens.fontWeight.semibold, "&:hover": { bgcolor: tokens.bg.hover } }}
              >
                Edit
              </Button>
              <Button
                size="small"
                startIcon={<DeleteIcon sx={{ fontSize: 15 }} />}
                onClick={() => setShowDeleteConfirm(true)}
                sx={{ fontSize: tokens.fontSize.sm2, textTransform: "none", color: tokens.status.danger, fontWeight: tokens.fontWeight.semibold, "&:hover": { bgcolor: tokens.status.dangerBg } }}
              >
                Delete
              </Button>
            </Stack>
          )}
        </Box>

        {/* ── Scrollable content ── */}
        <Box sx={{ flex: 1, overflow: "auto" }}>
          {/* Comparison Section — conditional per sub-type */}
          <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
            {item.classificationSubType === "WRONG_METAL_SPLIT" ? (
              <>
                {/* Shared HTS */}
                {item.filedHtsCode && (
                  <Box sx={{ textAlign: "center", mb: 1 }}>
                    <Typography sx={hdr}>HTS Code</Typography>
                    <HTSCodeWithTooltip htsCode={item.filedHtsCode}>
                      <Typography sx={{ fontSize: "1rem", fontWeight: tokens.fontWeight.heavy, fontFamily: "monospace", mt: 0.25, color: tokens.brand.primary, cursor: "pointer", "&:hover": { textDecoration: "underline" } }}>
                        {item.filedHtsCode}
                      </Typography>
                    </HTSCodeWithTooltip>
                  </Box>
                )}
                {/* Filed Metal % → Correct Metal % */}
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box sx={{ flex: 1, border: `1px solid ${tokens.border.default}`, borderRadius: tokens.radius.md, p: 1.5, textAlign: "center" }}>
                    <Typography sx={hdr}>Filed Metal %</Typography>
                    <Typography sx={{ fontSize: "1rem", fontWeight: tokens.fontWeight.heavy, mt: 0.25, color: tokens.text.primary }}>
                      {item.filedMetalPercentage != null ? `${Math.round(item.filedMetalPercentage * 100)}%` : "N/A"}
                    </Typography>
                    <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.muted, mt: 0.25 }}>
                      Derived from value split
                    </Typography>
                  </Box>
                  <ArrowIcon sx={{ color: tokens.text.disabled, fontSize: 20, flexShrink: 0 }} />
                  <Box sx={{ flex: 1, border: "1px solid #D1FAE5", borderRadius: tokens.radius.md, p: 1.5, textAlign: "center" }}>
                    <Typography sx={hdr}>Correct Metal %</Typography>
                    <Typography sx={{ fontSize: "1rem", fontWeight: tokens.fontWeight.heavy, mt: 0.25, color: tokens.status.success }}>
                      {item.correctMetalPercentage != null ? `${Math.round(item.correctMetalPercentage * 100)}%` : "N/A"}
                    </Typography>
                    {item.metalPercentageSource && (
                      <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.muted, mt: 0.25 }}>
                        Source: {item.metalPercentageSource}
                      </Typography>
                    )}
                  </Box>
                </Stack>
              </>
            ) : item.classificationSubType === "WRONG_STACKING" ? (
              <>
                {/* Shared HTS */}
                {item.filedHtsCode && (
                  <Box sx={{ textAlign: "center", mb: 1 }}>
                    <Typography sx={hdr}>HTS Code</Typography>
                    <HTSCodeWithTooltip htsCode={item.filedHtsCode}>
                      <Typography sx={{ fontSize: "1rem", fontWeight: tokens.fontWeight.heavy, fontFamily: "monospace", mt: 0.25, color: tokens.brand.primary, cursor: "pointer", "&:hover": { textDecoration: "underline" } }}>
                        {item.filedHtsCode}
                      </Typography>
                    </HTSCodeWithTooltip>
                  </Box>
                )}
                {/* Filed Ch.99 Codes → Expected Ch.99 Codes */}
                <Stack direction="row" spacing={1.5} alignItems="stretch">
                  <Box sx={{ flex: 1, border: `1px solid ${tokens.border.default}`, borderRadius: tokens.radius.md, p: 1.5 }}>
                    <Typography sx={{ ...hdr, textAlign: "center" }}>Filed Ch.99 Codes</Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1, justifyContent: "center" }}>
                      {(actualFiledCh99Codes && actualFiledCh99Codes.length > 0) ? actualFiledCh99Codes.map((code) => {
                        const normalizedCode = code.replace(/\./g, "");
                        const normalizedCorrect = (item.correctCh99Codes || []).map((c) => c.replace(/\./g, ""));
                        const isMissing = normalizedCorrect.length > 0 && !normalizedCorrect.includes(normalizedCode);
                        return (
                          <Chip key={code} label={code} size="small" sx={{
                            height: 22, fontSize: tokens.fontSize.xs, fontWeight: tokens.fontWeight.medium, borderRadius: 0.5, fontFamily: "monospace",
                            ...(isMissing ? { bgcolor: "#FEE2E2", color: tokens.status.danger, border: "1px solid #FECACA" } : { bgcolor: tokens.bg.muted, color: tokens.text.body }),
                          }} />
                        );
                      }) : <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.muted }}>None</Typography>}
                    </Box>
                  </Box>
                  <ArrowIcon sx={{ color: tokens.text.disabled, fontSize: 20, flexShrink: 0, alignSelf: "center" }} />
                  <Box sx={{ flex: 1, border: "1px solid #D1FAE5", borderRadius: tokens.radius.md, p: 1.5 }}>
                    <Typography sx={{ ...hdr, textAlign: "center" }}>Expected Ch.99 Codes</Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1, justifyContent: "center" }}>
                      {(item.correctCh99Codes && item.correctCh99Codes.length > 0) ? item.correctCh99Codes.map((code) => {
                        const normalizedCode = code.replace(/\./g, "");
                        const normalizedFiled = (actualFiledCh99Codes || []).map((c) => c.replace(/\./g, ""));
                        const isNew = normalizedFiled.length > 0 && !normalizedFiled.includes(normalizedCode);
                        return (
                          <Chip key={code} label={code} size="small" sx={{
                            height: 22, fontSize: tokens.fontSize.xs, fontWeight: tokens.fontWeight.medium, borderRadius: 0.5, fontFamily: "monospace",
                            ...(isNew ? { bgcolor: "#D1FAE5", color: "#065F46", border: "1px solid #A7F3D0" } : { bgcolor: tokens.bg.muted, color: tokens.text.body }),
                          }} />
                        );
                      }) : <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.muted }}>None</Typography>}
                    </Box>
                  </Box>
                </Stack>
                {/* Stacking Issue Type label */}
                {item.stackingIssueType && (
                  <Box sx={{ textAlign: "center", mt: 1.5 }}>
                    <Chip
                      label={item.stackingIssueType === "MISSING_CODE" ? "Missing Code" : item.stackingIssueType === "EXTRA_CODE" ? "Extra Code" : "Wrong Code"}
                      size="small"
                      sx={{
                        height: 22, fontSize: tokens.fontSize.xs, fontWeight: tokens.fontWeight.bold, borderRadius: 0.5,
                        bgcolor: "#EDE9FE", color: "#5B21B6", border: "1px solid #DDD6FE",
                      }}
                    />
                  </Box>
                )}
              </>
            ) : (
              /* Default: WRONG_HTS_CODE — Filed HTS → Correct HTS */
              <Stack direction="row" spacing={1.5} alignItems="center">
                {/* Filed HTS */}
                <Box sx={{ flex: 1, border: `1px solid ${tokens.border.default}`, borderRadius: tokens.radius.md, p: 1.5, textAlign: "center" }}>
                  <Typography sx={hdr}>Filed HTS</Typography>
                  {item.filedHtsCode ? (
                    <HTSCodeWithTooltip htsCode={item.filedHtsCode}>
                      <Typography sx={{ fontSize: "1rem", fontWeight: tokens.fontWeight.heavy, fontFamily: "monospace", mt: 0.25, color: tokens.brand.primary, cursor: "pointer", "&:hover": { textDecoration: "underline" } }}>
                        {item.filedHtsCode}
                      </Typography>
                    </HTSCodeWithTooltip>
                  ) : (
                    <Typography sx={{ fontSize: "1rem", fontWeight: tokens.fontWeight.heavy, fontFamily: "monospace", mt: 0.25, color: tokens.text.primary }}>N/A</Typography>
                  )}
                  {actualPaidRate != null ? (
                    <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.muted, mt: 0.25 }}>
                      Rate paid: {(actualPaidRate * 100).toFixed(2)}%
                    </Typography>
                  ) : filedDutyCalc ? (
                    <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.muted, mt: 0.25 }}>
                      Rate: {(filedDutyCalc.totalDutyRate * 100).toFixed(2)}%
                    </Typography>
                  ) : null}
                </Box>

                <ArrowIcon sx={{ color: tokens.text.disabled, fontSize: 20, flexShrink: 0 }} />

                {/* Correct HTS */}
                <Box sx={{ flex: 1, border: `1px solid ${tokens.border.default}`, borderRadius: tokens.radius.md, p: 1.5, textAlign: "center" }}>
                  <Typography sx={hdr}>Correct HTS</Typography>
                  {item.correctHtsCode ? (
                    <HTSCodeWithTooltip htsCode={item.correctHtsCode}>
                      <Typography sx={{ fontSize: "1rem", fontWeight: tokens.fontWeight.heavy, fontFamily: "monospace", mt: 0.25, color: tokens.brand.primary, cursor: "pointer", "&:hover": { textDecoration: "underline" } }}>
                        {item.correctHtsCode}
                      </Typography>
                    </HTSCodeWithTooltip>
                  ) : (
                    <Typography sx={{ fontSize: "1rem", fontWeight: tokens.fontWeight.heavy, fontFamily: "monospace", mt: 0.25, color: tokens.status.success }}>N/A</Typography>
                  )}
                  {correctDutyCalc ? (
                    (() => {
                      const isNonMetal = affectedLineCh99Codes && !affectedLineCh99Codes.hasSec232;
                      const applicableRate = isNonMetal
                        ? correctDutyCalc.additionalTariffs
                            .filter((t) => !/\b232\b/.test(t.columnName))
                            .reduce((sum, t) => sum + t.rate, 0)
                        : correctDutyCalc.totalDutyRate;
                      return (
                        <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.status.success, mt: 0.25 }}>
                          Rate: {(applicableRate * 100).toFixed(2)}%
                        </Typography>
                      );
                    })()
                  ) : null}
                </Box>
              </Stack>
            )}

            {/* Edit mode: duty rate + line number fields below cards */}
            {isEditMode && (
              <Stack direction="row" spacing={2} sx={{ mt: 1.5 }} justifyContent="center" alignItems="center" flexWrap="wrap">
                <TextField label="Filed Duty Rate" type="number" value={editFiledDutyRate} onChange={(e) => setEditFiledDutyRate(e.target.value)} size="small" sx={{ width: 140, ...numberFieldSx }} InputProps={{ endAdornment: <Typography sx={{ color: tokens.text.secondary }}>%</Typography> }} />
                <TextField label="Correct Duty Rate" type="number" value={editCorrectDutyRate} onChange={(e) => setEditCorrectDutyRate(e.target.value)} size="small" sx={{ width: 140, ...numberFieldSx }} InputProps={{ endAdornment: <Typography sx={{ color: tokens.text.secondary }}>%</Typography> }} />
                <TextField label="Line Number" type="number" value={editLineNumber} onChange={(e) => setEditLineNumber(e.target.value)} size="small" sx={{ width: 100, ...numberFieldSx }} placeholder="e.g. 1" />
              </Stack>
            )}

            {/* Duty Impact + Declared Value */}
            <Stack direction="row" spacing={2} sx={{ mt: 1.5, justifyContent: "center" }}>
              <Box sx={{ textAlign: "center" }}>
                <Typography sx={hdr}>Duty Impact</Typography>
                {metalSplitDutyImpact ? (
                  <>
                    <Typography sx={{
                      fontSize: tokens.fontSize.sm2,
                      fontWeight: tokens.fontWeight.heavy,
                      mt: 0.25,
                      color: metalSplitDutyImpact.isUnderpayment ? tokens.status.danger : tokens.status.success,
                    }}>
                      {formatCurrencyPrecise(Math.abs(metalSplitDutyImpact.dutyImpact))}
                    </Typography>
                    <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.muted }}>
                      {metalSplitDutyImpact.isUnderpayment ? "Underpaid" : "Overpaid"} ({metalSplitDutyImpact.rateChange > 0 ? "+" : ""}{(metalSplitDutyImpact.rateChange * 100).toFixed(2)}% net rate)
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography sx={{
                      fontSize: tokens.fontSize.sm2,
                      fontWeight: tokens.fontWeight.heavy,
                      mt: 0.25,
                      color: item.dutyImpactType === "UNDERPAYMENT" ? tokens.status.danger : item.dutyImpactType === "OVERPAYMENT" ? tokens.status.warning : tokens.text.subtle,
                    }}>
                      {(item.dutyDifference ?? 0) > 0 ? formatCurrencyPrecise(item.dutyDifference) : "-"}
                    </Typography>
                    {item.dutyImpactType && item.dutyImpactType !== "NO_DIFFERENCE" && (
                      <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.muted }}>
                        {item.dutyImpactType === "UNDERPAYMENT" ? "Underpaid" : "Overpaid"}
                      </Typography>
                    )}
                  </>
                )}
              </Box>
              {item.declaredValue != null && (
                <Box sx={{ textAlign: "center" }}>
                  <Typography sx={hdr}>
                    {item.lineNumbers && item.lineNumbers.length > 1
                      ? `Combined Value (${item.lineNumbers.length} lines)`
                      : item.isMetalSplit ? "Combined Declared Value"
                      : "Declared Value"}
                  </Typography>
                  <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.heavy, color: tokens.text.primary, mt: 0.25 }}>
                    {formatCurrencyPrecise(item.declaredValue)}
                  </Typography>
                </Box>
              )}
            </Stack>
          </Box>

          {/* Action buttons */}
          {isEditMode ? (
            <Box sx={{ mb: 2, px: 2.5, pt: 1.5 }}>
              <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.bold, color: tokens.text.primary, mb: 2, textAlign: "center" }}>
                Edit Classification Values
              </Typography>
              <Stack direction="row" spacing={2} justifyContent="center" sx={{ mb: 2 }} flexWrap="wrap">
                <TextField label="Filed HTS Code" value={editFiledHtsCode} onChange={(e) => setEditFiledHtsCode(e.target.value)} size="small" sx={{ width: 160 }} />
                <TextField label="Correct HTS Code" value={editCorrectHtsCode} onChange={(e) => setEditCorrectHtsCode(e.target.value)} size="small" sx={{ width: 160 }} />
              </Stack>
              <Stack direction="row" spacing={2} justifyContent="center" sx={{ mb: 2 }} flexWrap="wrap">
                <TextField label="Filed Duty Rate" type="number" value={editFiledDutyRate} onChange={(e) => setEditFiledDutyRate(e.target.value)} size="small" sx={{ width: 140 }} InputProps={{ endAdornment: <Typography sx={{ color: tokens.text.secondary }}>%</Typography> }} />
                <TextField label="Correct Duty Rate" type="number" value={editCorrectDutyRate} onChange={(e) => setEditCorrectDutyRate(e.target.value)} size="small" sx={{ width: 140 }} InputProps={{ endAdornment: <Typography sx={{ color: tokens.text.secondary }}>%</Typography> }} />
                <TextField label="Line Number" type="number" value={editLineNumber} onChange={(e) => setEditLineNumber(e.target.value)} size="small" sx={{ width: 100 }} placeholder="e.g. 1" />
              </Stack>
              <Stack direction="row" spacing={1.5} justifyContent="center">
                <Button variant="outlined" onClick={handleCancelEdit} disabled={isUpdatingValues} sx={{ borderRadius: tokens.radius.md, color: tokens.text.body, borderColor: tokens.border.default }}>Cancel</Button>
                <Button variant="contained" onClick={handleSaveClassificationValues} disabled={isUpdatingValues} sx={{ bgcolor: tokens.brand.primary, "&:hover": { bgcolor: tokens.brand.primaryHover }, fontWeight: tokens.fontWeight.bold, borderRadius: tokens.radius.md }}>
                  {isUpdatingValues ? <CircularProgress size={20} color="inherit" /> : "Save"}
                </Button>
              </Stack>
            </Box>
          ) : showDeleteConfirm ? (
            <Box sx={{ textAlign: "center", mb: 2, px: 2.5, pt: 1.5 }}>
              <Typography sx={{ mb: 1, color: tokens.status.danger, fontWeight: tokens.fontWeight.bold, fontSize: tokens.fontSize.sm2 }}>Delete this discrepancy?</Typography>
              <Typography sx={{ mb: 1.5, color: tokens.text.secondary, fontSize: tokens.fontSize.xs }}>This will remove it from the audit permanently.</Typography>
              <Stack direction="row" spacing={1.5} justifyContent="center">
                <Button variant="outlined" onClick={() => setShowDeleteConfirm(false)} disabled={isDeleting} sx={{ borderRadius: tokens.radius.md, color: tokens.text.body, borderColor: tokens.border.default }}>Cancel</Button>
                <Button variant="contained" onClick={handleDelete} disabled={isDeleting} sx={{ bgcolor: tokens.status.danger, "&:hover": { bgcolor: tokens.status.dangerHover }, fontWeight: tokens.fontWeight.bold, borderRadius: tokens.radius.md }}>
                  {isDeleting ? <CircularProgress size={20} color="inherit" /> : "Delete"}
                </Button>
              </Stack>
            </Box>
          ) : (
            <Stack direction="row" spacing={1.5} justifyContent="center" sx={{ mb: 2, px: 2.5, pt: 1.5 }}>
              {/* Edit Manually — only for WRONG_HTS_CODE (edit form is HTS-code-specific) */}
              {(!item.classificationSubType || item.classificationSubType === "WRONG_HTS_CODE") && (
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={handleStartEdit}
                  sx={{ fontWeight: tokens.fontWeight.bold, borderRadius: tokens.radius.md, px: 2.5, py: 1, color: tokens.text.body, borderColor: tokens.border.default, "&:hover": { borderColor: tokens.text.disabled, bgcolor: tokens.bg.hover } }}
                >
                  Edit Manually
                </Button>
              )}
              <Button
                variant="outlined"
                startIcon={<DeleteIcon />}
                onClick={() => setShowDeleteConfirm(true)}
                sx={{ fontWeight: tokens.fontWeight.bold, borderRadius: tokens.radius.md, px: 2.5, py: 1, color: tokens.status.danger, borderColor: "#FECACA", "&:hover": { borderColor: tokens.status.danger, bgcolor: "#FEF2F2" } }}
              >
                Delete
              </Button>
            </Stack>
          )}

          {/* ── Metal Split Rate Comparison — per-line with Expected vs ACE ── */}
          {item.classificationSubType === "WRONG_METAL_SPLIT" && metalSplitAnalysis && (() => {
            const dutyCalc = correctDutyCalc || filedDutyCalc;

            const lineConfigs = [
              {
                type: "METAL" as const, label: "Metal Line", entryLine: metalSplitAnalysis.metalLine,
                filedPct: metalSplitAnalysis.filed.metalPct, correctPct: metalSplitAnalysis.expected.metalPct,
                aceValue: metalSplitAnalysis.filed.metalValue, expectedValue: metalSplitAnalysis.expected.metalValue,
                aceDuty: metalSplitAnalysis.filed.metalDuty, expectedDuty: metalSplitAnalysis.expected.metalDuty,
                borderColor: "#7C3AED", headerBg: "#EDE9FE", headerColor: "#6D28D9",
              },
              {
                type: "NONMETAL" as const, label: "Non-Metal Line", entryLine: metalSplitAnalysis.nonMetalLine,
                filedPct: 1 - metalSplitAnalysis.filed.metalPct, correctPct: 1 - metalSplitAnalysis.expected.metalPct,
                aceValue: metalSplitAnalysis.filed.nonMetalValue, expectedValue: metalSplitAnalysis.expected.nonMetalValue,
                aceDuty: metalSplitAnalysis.filed.nonMetalDuty, expectedDuty: metalSplitAnalysis.expected.nonMetalDuty,
                borderColor: tokens.border.default, headerBg: tokens.bg.hover, headerColor: tokens.text.body,
              },
            ];

            const numStyle = { fontSize: tokens.fontSize.xs, fontWeight: tokens.fontWeight.semibold, fontVariantNumeric: "tabular-nums" as const, textAlign: "right" as const, flexShrink: 0 };
            const colW = { rate: 60, aceRate: 60, expectedAmt: 76, aceAmt: 76 };

            return (
              <Box sx={sectionSx}>
                {/* Metal allocation banner */}
                <Box sx={{
                  mb: 1.5, p: 1.25, bgcolor: tokens.bg.muted, borderRadius: tokens.radius.md,
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 1,
                }}>
                  <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.bold, color: tokens.text.secondary }}>
                    Metal Allocation
                  </Typography>
                  <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.heavy, color: tokens.text.primary }}>
                    {Math.round(metalSplitAnalysis.filed.metalPct * 100)}%
                  </Typography>
                  <ArrowIcon sx={{ fontSize: 14, color: tokens.text.disabled }} />
                  <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.heavy, color: tokens.status.success }}>
                    {Math.round(metalSplitAnalysis.expected.metalPct * 100)}%
                  </Typography>
                </Box>

                <Stack spacing={2}>
                  {lineConfigs.map((lc) => {
                    const bLine = dutyCalc?.breakdownLines?.find((bl) => bl.lineType === lc.type);
                    const ch99Codes = lc.entryLine?.chapter99Codes || [];

                    // Authority breakdown from duty calculator (rates are same for both ACE and expected — only values differ)
                    const rules = bLine?.applicableRules?.slice().sort((a, b) => b.rate - a.rate) || [];
                    const rows = rules.map((rule) => ({
                      authority: rule.columnName,
                      rate: rule.rate,
                      aceDollar: lc.aceValue * rule.rate,
                      expectedDollar: lc.expectedValue * rule.rate,
                    }));

                    const lineDiff = lc.aceDuty - lc.expectedDuty;

                    return (
                      <Box key={lc.type} sx={{ border: `1px solid ${lc.borderColor}`, borderRadius: tokens.radius.lg, overflow: "hidden" }}>
                        {/* Card header */}
                        <Box sx={{
                          px: 2, py: 1, display: "flex", alignItems: "center", justifyContent: "space-between",
                          bgcolor: lc.headerBg, borderBottom: `1px solid ${lc.borderColor}`,
                        }}>
                          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexWrap: "wrap" }}>
                            <Typography sx={{ fontWeight: tokens.fontWeight.bold, fontSize: tokens.fontSize.sm2, color: lc.headerColor }}>
                              {lc.label}
                            </Typography>
                            {ch99Codes.map((code) => (
                              <Chip key={code} label={code} size="small" sx={{
                                height: 18, fontSize: tokens.fontSize["2xs"], fontWeight: tokens.fontWeight.book,
                                borderRadius: 0.5, fontFamily: "monospace",
                                bgcolor: /232/.test(code) ? "#DDD6FE" : tokens.bg.muted,
                                color: /232/.test(code) ? "#5B21B6" : tokens.text.secondary,
                              }} />
                            ))}
                          </Stack>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.muted }}>
                              {Math.round(lc.filedPct * 100)}%
                            </Typography>
                            <ArrowIcon sx={{ fontSize: 12, color: tokens.text.disabled }} />
                            <Typography sx={{ fontSize: tokens.fontSize.xs, fontWeight: tokens.fontWeight.bold, color: tokens.status.success }}>
                              {Math.round(lc.correctPct * 100)}%
                            </Typography>
                          </Stack>
                        </Box>

                        <Box sx={{ p: 2 }}>
                          {/* Summary: ACE Value | Expected Value | ACE Duty | Expected Duty */}
                          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "1fr 1fr 1fr 1fr" }, gap: 1, mb: 1.5 }}>
                            <Box>
                              <Typography sx={{ fontSize: tokens.fontSize["2xs"], color: tokens.text.muted, textTransform: "uppercase", letterSpacing: 0.3 }}>ACE Value</Typography>
                              <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.bold }}>{formatCurrencyPrecise(lc.aceValue)}</Typography>
                            </Box>
                            <Box>
                              <Typography sx={{ fontSize: tokens.fontSize["2xs"], color: tokens.text.muted, textTransform: "uppercase", letterSpacing: 0.3 }}>Expected Value</Typography>
                              <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.bold, color: tokens.status.success }}>{formatCurrencyPrecise(lc.expectedValue)}</Typography>
                            </Box>
                            <Box>
                              <Typography sx={{ fontSize: tokens.fontSize["2xs"], color: tokens.text.muted, textTransform: "uppercase", letterSpacing: 0.3 }}>ACE Duty</Typography>
                              <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.bold }}>{formatCurrencyPrecise(lc.aceDuty)}</Typography>
                            </Box>
                            <Box>
                              <Typography sx={{ fontSize: tokens.fontSize["2xs"], color: tokens.text.muted, textTransform: "uppercase", letterSpacing: 0.3 }}>Expected Duty</Typography>
                              <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.bold, color: tokens.status.success }}>{formatCurrencyPrecise(lc.expectedDuty)}</Typography>
                            </Box>
                          </Box>

                          {/* Rate breakdown table */}
                          {rows.length > 0 && (
                            <>
                              <Stack direction="row" sx={{ mb: 0.5 }}>
                                <Typography sx={{ fontSize: tokens.fontSize["2xs"], color: tokens.text.muted, flex: 1 }}>Authority</Typography>
                                <Typography sx={{ fontSize: tokens.fontSize["2xs"], color: tokens.text.muted, width: colW.rate, textAlign: "right" }}>Rate</Typography>
                                <Typography sx={{ fontSize: tokens.fontSize["2xs"], color: tokens.text.muted, width: colW.aceAmt, textAlign: "right" }}>ACE $</Typography>
                                <Typography sx={{ fontSize: tokens.fontSize["2xs"], color: tokens.text.muted, width: colW.expectedAmt, textAlign: "right" }}>Expected $</Typography>
                              </Stack>
                              <Stack spacing={0.25}>
                                {rows.map(({ authority, rate, aceDollar, expectedDollar }, ri) => {
                                  const amtDiff = Math.abs(aceDollar - expectedDollar) > 1;
                                  return (
                                    <Stack key={ri} direction="row" alignItems="baseline">
                                      <Tooltip title={authority} placement="top-start" arrow>
                                        <Typography noWrap sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.secondary, flex: 1, minWidth: 0, mr: 1 }}>{authority}</Typography>
                                      </Tooltip>
                                      <Typography sx={{ ...numStyle, width: colW.rate }}>{(rate * 100).toFixed(2)}%</Typography>
                                      <Typography sx={{ ...numStyle, width: colW.aceAmt }}>{formatCurrencyPrecise(aceDollar)}</Typography>
                                      <Typography sx={{ ...numStyle, width: colW.expectedAmt, ...(amtDiff && { color: tokens.status.success }) }}>{formatCurrencyPrecise(expectedDollar)}</Typography>
                                    </Stack>
                                  );
                                })}
                                {/* Total row */}
                                <Divider sx={{ my: 0.5, borderColor: tokens.border.light }} />
                                <Stack direction="row" alignItems="baseline">
                                  <Typography sx={{ fontSize: tokens.fontSize.xs, fontWeight: tokens.fontWeight.bold, color: tokens.text.primary, flex: 1 }}>Total</Typography>
                                  <Typography sx={{ ...numStyle, fontWeight: tokens.fontWeight.bold, width: colW.rate }}>
                                    {bLine ? `${(bLine.totalRate * 100).toFixed(2)}%` : "—"}
                                  </Typography>
                                  <Typography sx={{ ...numStyle, fontWeight: tokens.fontWeight.bold, width: colW.aceAmt }}>
                                    {formatCurrencyPrecise(lc.aceDuty)}
                                  </Typography>
                                  <Typography sx={{ ...numStyle, fontWeight: tokens.fontWeight.bold, width: colW.expectedAmt, color: tokens.status.success }}>
                                    {formatCurrencyPrecise(lc.expectedDuty)}
                                  </Typography>
                                </Stack>
                                {Math.abs(lineDiff) > 0.01 && (
                                  <Stack direction="row" alignItems="baseline">
                                    <Typography sx={{ fontSize: tokens.fontSize.xs, color: lineDiff > 0 ? tokens.status.success : tokens.status.danger, flex: 1 }}>
                                      {lineDiff > 0 ? "Overpaid" : "Underpaid"}
                                    </Typography>
                                    <Typography sx={{ fontSize: tokens.fontSize.xs, fontWeight: tokens.fontWeight.bold, fontVariantNumeric: "tabular-nums", color: lineDiff > 0 ? tokens.status.success : tokens.status.danger }}>
                                      {formatCurrencyPrecise(Math.abs(lineDiff))}
                                    </Typography>
                                  </Stack>
                                )}
                              </Stack>
                            </>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Stack>

                {/* Combined totals */}
                <Box sx={{ mt: 1.5, border: `1px solid ${tokens.border.default}`, borderRadius: tokens.radius.lg, p: 2 }}>
                  <Stack direction="row" sx={{ mb: 0.5 }}>
                    <Typography sx={{ fontSize: tokens.fontSize["2xs"], color: tokens.text.muted, flex: 1 }}></Typography>
                    <Typography sx={{ fontSize: tokens.fontSize["2xs"], color: tokens.text.muted, width: 80, textAlign: "right" }}>ACE Paid</Typography>
                    <Typography sx={{ fontSize: tokens.fontSize["2xs"], color: tokens.text.muted, width: 80, textAlign: "right" }}>Expected</Typography>
                  </Stack>
                  <Stack spacing={0.25}>
                    <Stack direction="row" alignItems="baseline">
                      <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.bold, color: tokens.text.primary, flex: 1 }}>Combined Duty</Typography>
                      <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.bold, fontVariantNumeric: "tabular-nums", width: 80, textAlign: "right", flexShrink: 0 }}>
                        {formatCurrencyPrecise(metalSplitAnalysis.filed.total)}
                      </Typography>
                      <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.bold, fontVariantNumeric: "tabular-nums", width: 80, textAlign: "right", flexShrink: 0, color: tokens.status.success }}>
                        {formatCurrencyPrecise(metalSplitAnalysis.expected.total)}
                      </Typography>
                    </Stack>
                    {metalSplitAnalysis.difference !== 0 && (
                      <Stack direction="row" alignItems="baseline">
                        <Typography sx={{ fontSize: tokens.fontSize.xs, color: metalSplitAnalysis.difference > 0 ? tokens.status.success : tokens.status.danger, flex: 1 }}>
                          {metalSplitAnalysis.difference > 0 ? "Overpaid" : "Underpaid"}
                        </Typography>
                        <Typography sx={{ fontSize: tokens.fontSize.xs, fontWeight: tokens.fontWeight.bold, fontVariantNumeric: "tabular-nums", color: metalSplitAnalysis.difference > 0 ? tokens.status.success : tokens.status.danger }}>
                          {formatCurrencyPrecise(Math.abs(metalSplitAnalysis.difference))}
                        </Typography>
                      </Stack>
                    )}
                  </Stack>
                </Box>
              </Box>
            );
          })()}

          {/* Entry Lines table */}
          {item.entryLineData && item.entryLineData.length > 0 && (
            <Box sx={{ mb: 1.5, px: 2, pt: 1 }}>
              <Typography sx={{ fontSize: tokens.fontSize.xs, fontWeight: tokens.fontWeight.bold, color: tokens.text.primary, mb: 0.5 }}>
                Entry Lines ({item.entryLineData.length})
              </Typography>
              <Box sx={tableContainerSx}>
                <Box sx={tableCompactSx}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Line #</TableCell>
                        <TableCell>HTS Code</TableCell>
                        <TableCell>Ch.99</TableCell>
                        <TableCell>Mfg ID</TableCell>
                        <TableCell>Origin</TableCell>
                        <TableCell align="right">Goods Value</TableCell>
                        <TableCell align="right">Duty</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {item.entryLineData.map((line) => {
                        const isAffected =
                          item.lineNumbers?.includes(line.lineNumber) ||
                          (item.entryLines || []).some((el) => el.toLowerCase() === (line.entryLine || "").toLowerCase());
                        return (
                          <TableRow key={line.lineNumber} sx={{ ...(isAffected && { bgcolor: "#FEF2F2 !important" }) }}>
                            <TableCell sx={{ fontWeight: tokens.fontWeight.medium, fontSize: tokens.fontSize.sm2 }}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                {line.lineNumber}
                                {line.hasSec232 && <Chip label="232" size="small" sx={{ height: 18, fontSize: tokens.fontSize["2xs"], fontWeight: tokens.fontWeight.book, borderRadius: 0.5, bgcolor: tokens.status.infoBg, color: tokens.chip.blue.text }} />}
                                {isAffected && <Chip label="Affected" size="small" sx={{ height: 18, fontSize: tokens.fontSize["2xs"], fontWeight: tokens.fontWeight.book, borderRadius: 0.5, bgcolor: tokens.status.dangerBg, color: tokens.status.danger }} />}
                              </Box>
                            </TableCell>
                            <TableCell sx={{ fontWeight: tokens.fontWeight.medium, fontSize: tokens.fontSize.sm2 }}>{line.htsCode ? formatHTSCode(line.htsCode) : "-"}</TableCell>
                            <TableCell>
                              {line.chapter99Codes && line.chapter99Codes.length > 0 ? (
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                  {line.chapter99Codes.map((code) => {
                                    // For stacking discrepancies, highlight extra codes on affected lines
                                    // Code is "extra" if it's on the affected line but not in the AI's correct set
                                    const normalizedCode = code.replace(/\./g, "");
                                    const normalizedCorrect = (item.correctCh99Codes || []).map((c) => c.replace(/\./g, ""));
                                    const isExtraCode = isAffected && item.classificationSubType === "WRONG_STACKING" && normalizedCorrect.length > 0 && !normalizedCorrect.includes(normalizedCode);
                                    return (
                                      <Chip key={code} label={code} size="small" sx={{
                                        height: 18, fontSize: tokens.fontSize["2xs"], fontWeight: tokens.fontWeight.book, borderRadius: 0.5,
                                        ...(isExtraCode ? { bgcolor: "#FEE2E2", color: tokens.status.danger, border: "1px solid #FECACA" } : { bgcolor: tokens.bg.muted, color: tokens.text.body }),
                                      }} />
                                    );
                                  })}
                                </Box>
                              ) : <Typography sx={{ fontSize: "inherit", color: tokens.text.subtle }}>-</Typography>}
                            </TableCell>
                            <TableCell sx={{ fontWeight: tokens.fontWeight.medium, fontSize: tokens.fontSize.sm2 }}>{line.manufacturerId || "-"}</TableCell>
                            <TableCell sx={{ color: tokens.text.secondary }}>{line.countryOfOrigin || "-"}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: tokens.fontWeight.medium, fontSize: tokens.fontSize.sm2 }}>{line.goodsValue != null ? formatCurrencyPrecise(line.goodsValue) : "-"}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: tokens.fontWeight.medium, fontSize: tokens.fontSize.sm2, color: tokens.status.success }}>{line.lineDutyAmount != null ? formatCurrencyPrecise(line.lineDutyAmount) : "-"}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Box>
              </Box>
            </Box>
          )}

          {/* Duty Rate Breakdown — only for WRONG_HTS_CODE (same HTS shows identical rates for other sub-types) */}
          {(!item.classificationSubType || item.classificationSubType === "WRONG_HTS_CODE") && (filedDutyInput || correctDutyInput) && (
            <Box sx={{ mb: 1.5, px: 2 }}>
              <Typography sx={{ fontSize: tokens.fontSize.xs, fontWeight: tokens.fontWeight.bold, color: tokens.text.primary, mb: 0.5 }}>
                Duty Rate Breakdown
              </Typography>
              <Stack direction="row" spacing={1.5}>
                {/* Filed HTS Duty Calc */}
                {filedDutyInput && (
                  <Box sx={{ flex: 1, border: `1px solid ${tokens.border.default}`, borderRadius: tokens.radius.md, overflow: "hidden" }}>
                    <Box sx={{ px: 1.5, py: 0.5, bgcolor: tokens.bg.hover, borderBottom: `1px solid ${tokens.border.default}` }}>
                      <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.bold, color: tokens.text.body }}>
                        Filed HTS ({item.filedHtsCode})
                      </Typography>
                    </Box>
                    <Box sx={{ p: 1.5 }}>
                      {filedDutyFetching ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
                          <CircularProgress size={18} />
                        </Box>
                      ) : filedDutyCalc ? (
                        (() => {
                          const applicableRate = filedDutyCalc.additionalTariffs
                            .filter((t) => isTariffApplicable(t.columnName))
                            .reduce((sum, t) => sum + t.rate, 0);
                          const hasNonApplicable = filedDutyCalc.additionalTariffs.some((t) => !isTariffApplicable(t.columnName));
                          return (
                            <Stack spacing={0.5}>
                              {filedDutyCalc.additionalTariffs.map((t, i) => {
                                const applicable = isTariffApplicable(t.columnName);
                                return (
                                  <Stack key={i} direction="row" justifyContent="space-between" sx={{ opacity: applicable ? 1 : 0.4 }}>
                                    <Typography sx={{ fontSize: tokens.fontSize.base, color: tokens.text.secondary, ...((!applicable) && { textDecoration: "line-through" }) }}>
                                      {t.columnName}{!applicable ? " (not filed)" : ""}
                                    </Typography>
                                    <Typography sx={{ fontSize: tokens.fontSize.base, fontWeight: tokens.fontWeight.bold, color: applicable ? tokens.text.body : tokens.text.disabled }}>{(t.rate * 100).toFixed(2)}%</Typography>
                                  </Stack>
                                );
                              })}
                              {hasNonApplicable ? (
                                <>
                                  <Stack direction="row" justifyContent="space-between" sx={{ pt: 1, mt: 0.5, borderTop: `1px solid ${tokens.border.default}` }}>
                                    <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.bold, color: tokens.text.primary }}>Applicable Rate</Typography>
                                    <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.bold, color: tokens.text.primary }}>{(applicableRate * 100).toFixed(2)}%</Typography>
                                  </Stack>
                                  {actualPaidRate != null && (
                                    <Stack direction="row" justifyContent="space-between">
                                      <Typography sx={{ fontSize: tokens.fontSize.base, color: tokens.text.muted }}>ACE Rate Paid</Typography>
                                      <Typography sx={{ fontSize: tokens.fontSize.base, fontWeight: tokens.fontWeight.bold, color: tokens.text.muted }}>{(actualPaidRate * 100).toFixed(2)}%</Typography>
                                    </Stack>
                                  )}
                                </>
                              ) : (
                                <Stack direction="row" justifyContent="space-between" sx={{ pt: 1, mt: 0.5, borderTop: `1px solid ${tokens.border.default}` }}>
                                  <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.bold, color: tokens.text.primary }}>Total</Typography>
                                  <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.bold, color: tokens.text.primary }}>{(filedDutyCalc.totalDutyRate * 100).toFixed(2)}%</Typography>
                                </Stack>
                              )}
                            </Stack>
                          );
                        })()
                      ) : (
                        <Typography sx={{ color: tokens.text.muted, fontSize: tokens.fontSize.base }}>No data available</Typography>
                      )}
                    </Box>
                  </Box>
                )}
                {/* Correct HTS Duty Calc */}
                {correctDutyInput && (
                  <Box sx={{ flex: 1, border: "1px solid #D1FAE5", borderRadius: tokens.radius.md, overflow: "hidden" }}>
                    <Box sx={{ px: 1.5, py: 0.5, bgcolor: tokens.status.successBg, borderBottom: "1px solid #D1FAE5" }}>
                      <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.bold, color: tokens.status.success }}>
                        Correct HTS ({item.correctHtsCode})
                      </Typography>
                    </Box>
                    <Box sx={{ p: 1.5 }}>
                      {correctDutyFetching ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
                          <CircularProgress size={18} />
                        </Box>
                      ) : correctDutyCalc ? (
                        (() => {
                          // For correct HTS, we can't check filed Ch.99 codes (they'd be different).
                          // Instead, check if the affected line is non-metal — if so, exclude 232-type tariffs.
                          const isNonMetal = affectedLineCh99Codes && !affectedLineCh99Codes.hasSec232;
                          const is232Tariff = (name: string) => /\b232\b/.test(name);
                          const applicableRate = correctDutyCalc.additionalTariffs
                            .filter((t) => !(isNonMetal && is232Tariff(t.columnName)))
                            .reduce((sum, t) => sum + t.rate, 0);
                          const hasNonApplicable = isNonMetal && correctDutyCalc.additionalTariffs.some((t) => is232Tariff(t.columnName));
                          return (
                            <Stack spacing={0.5}>
                              {correctDutyCalc.additionalTariffs.map((t, i) => {
                                const applicable = !(isNonMetal && is232Tariff(t.columnName));
                                return (
                                  <Stack key={i} direction="row" justifyContent="space-between" sx={{ opacity: applicable ? 1 : 0.4 }}>
                                    <Typography sx={{ fontSize: tokens.fontSize.base, color: tokens.text.secondary, ...((!applicable) && { textDecoration: "line-through" }) }}>
                                      {t.columnName}{!applicable ? " (non-metal)" : ""}
                                    </Typography>
                                    <Typography sx={{ fontSize: tokens.fontSize.base, fontWeight: tokens.fontWeight.bold, color: applicable ? tokens.text.body : tokens.text.disabled }}>{(t.rate * 100).toFixed(2)}%</Typography>
                                  </Stack>
                                );
                              })}
                              {hasNonApplicable ? (
                                <Stack direction="row" justifyContent="space-between" sx={{ pt: 1, mt: 0.5, borderTop: `1px solid ${tokens.border.default}` }}>
                                  <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.bold, color: tokens.status.success }}>Applicable Rate</Typography>
                                  <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.bold, color: tokens.status.success }}>{(applicableRate * 100).toFixed(2)}%</Typography>
                                </Stack>
                              ) : (
                                <Stack direction="row" justifyContent="space-between" sx={{ pt: 1, mt: 0.5, borderTop: `1px solid ${tokens.border.default}` }}>
                                  <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.bold, color: tokens.status.success }}>Total</Typography>
                                  <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.bold, color: tokens.status.success }}>{(correctDutyCalc.totalDutyRate * 100).toFixed(2)}%</Typography>
                                </Stack>
                              )}
                            </Stack>
                          );
                        })()
                      ) : (
                        <Typography sx={{ color: tokens.text.muted, fontSize: tokens.fontSize.base }}>No data available</Typography>
                      )}
                    </Box>
                  </Box>
                )}
              </Stack>
            </Box>
          )}

          {/* Stacking Duty Rate Comparison — for WRONG_STACKING, compare rates with filed vs correct Ch.99 codes */}
          {item.classificationSubType === "WRONG_STACKING" && filedDutyInput && (
            <Box sx={{ mb: 1.5, px: 2 }}>
              <Typography sx={{ fontSize: tokens.fontSize.xs, fontWeight: tokens.fontWeight.bold, color: tokens.text.primary, mb: 0.5 }}>
                Tariff Rate Comparison
              </Typography>
              {filedDutyFetching ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
                  <CircularProgress size={18} />
                </Box>
              ) : filedDutyCalc ? (
                (() => {
                  // Helper: extract normalized Ch.99 code from a tariff column name
                  const extractCode = (name: string): string | null => {
                    const m = name.match(/\b(99\d{2}[\.\s]?\d{2}[\.\s]?\d{2,4})\b/);
                    return m ? m[1].replace(/[\.\s]/g, "") : null;
                  };
                  // Use actual entry line data as source of truth for filed codes
                  const filedSet = affectedLineCh99Codes?.codes ?? new Set<string>();
                  const correctSet = new Set((item.correctCh99Codes || []).map((c) => c.replace(/\./g, "")));

                  // Metal split context: non-metal lines exclude 232, metal lines always include 232
                  const is232Tariff = (name: string) => /\b232\b/.test(name);
                  const isNonMetalSplit = metalSplitContext?.isNonMetal === true;
                  const isMetalSplit = metalSplitContext?.isMetal === true;

                  // Split tariffs into: General Duty (always applies), and Ch.99 tariffs (apply based on codes)
                  const generalTariffs = filedDutyCalc.additionalTariffs.filter((t) => !extractCode(t.columnName));
                  const ch99Tariffs = filedDutyCalc.additionalTariffs.filter((t) => extractCode(t.columnName));

                  const generalRate = generalTariffs.reduce((s, t) => s + t.rate, 0);

                  // Filed side: General + tariffs matching filed Ch.99 codes
                  const filedApplicable = ch99Tariffs.filter((t) => filedSet.has(extractCode(t.columnName)!));
                  const filedRate = generalRate + filedApplicable.reduce((s, t) => s + t.rate, 0);

                  // Correct side: General + tariffs matching correct Ch.99 codes
                  // Non-metal lines: exclude 232 tariffs (they don't apply)
                  // Metal lines: always include 232 tariffs (they correctly apply to the metal portion)
                  const correctApplicable = ch99Tariffs.filter((t) => {
                    const code = extractCode(t.columnName);
                    if (isNonMetalSplit && is232Tariff(t.columnName)) return false;
                    // Metal lines: 232 always applies even if AI didn't include it in correctCh99Codes
                    if (isMetalSplit && is232Tariff(t.columnName)) return true;
                    if (!code || !correctSet.has(code)) return false;
                    return true;
                  });
                  const correctRate = generalRate + correctApplicable.reduce((s, t) => s + t.rate, 0);

                  return (
                    <Stack direction="row" spacing={1.5}>
                      {/* As Filed */}
                      <Box sx={{ flex: 1, border: `1px solid ${tokens.border.default}`, borderRadius: tokens.radius.md, overflow: "hidden" }}>
                        <Box sx={{ px: 1.5, py: 0.5, bgcolor: tokens.bg.hover, borderBottom: `1px solid ${tokens.border.default}` }}>
                          <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.bold, color: tokens.text.body }}>
                            As Filed
                          </Typography>
                        </Box>
                        <Box sx={{ p: 1.5 }}>
                          <Stack spacing={0.5}>
                            {generalTariffs.map((t, i) => (
                              <Stack key={`g-${i}`} direction="row" justifyContent="space-between">
                                <Typography sx={{ fontSize: tokens.fontSize.base, color: tokens.text.secondary }}>{t.columnName}</Typography>
                                <Typography sx={{ fontSize: tokens.fontSize.base, fontWeight: tokens.fontWeight.bold, color: tokens.text.body }}>{(t.rate * 100).toFixed(2)}%</Typography>
                              </Stack>
                            ))}
                            {ch99Tariffs.map((t, i) => {
                              const code = extractCode(t.columnName);
                              const isFiled = code ? filedSet.has(code) : false;
                              return (
                                <Stack key={`c-${i}`} direction="row" justifyContent="space-between" sx={{ opacity: isFiled ? 1 : 0.35 }}>
                                  <Typography sx={{ fontSize: tokens.fontSize.base, color: tokens.text.secondary, ...(!isFiled && { textDecoration: "line-through" }) }}>
                                    {t.columnName}
                                  </Typography>
                                  <Typography sx={{ fontSize: tokens.fontSize.base, fontWeight: tokens.fontWeight.bold, color: isFiled ? tokens.text.body : tokens.text.disabled }}>
                                    {(t.rate * 100).toFixed(2)}%
                                  </Typography>
                                </Stack>
                              );
                            })}
                            <Stack direction="row" justifyContent="space-between" sx={{ pt: 1, mt: 0.5, borderTop: `1px solid ${tokens.border.default}` }}>
                              <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.bold, color: tokens.text.primary }}>Filed Rate</Typography>
                              <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.bold, color: tokens.text.primary }}>{(filedRate * 100).toFixed(2)}%</Typography>
                            </Stack>
                            {actualPaidRate != null && (
                              <Stack direction="row" justifyContent="space-between">
                                <Typography sx={{ fontSize: tokens.fontSize.base, color: tokens.text.muted }}>ACE Rate Paid</Typography>
                                <Typography sx={{ fontSize: tokens.fontSize.base, fontWeight: tokens.fontWeight.bold, color: tokens.text.muted }}>{(actualPaidRate * 100).toFixed(2)}%</Typography>
                              </Stack>
                            )}
                          </Stack>
                        </Box>
                      </Box>
                      {/* Correct */}
                      <Box sx={{ flex: 1, border: "1px solid #D1FAE5", borderRadius: tokens.radius.md, overflow: "hidden" }}>
                        <Box sx={{ px: 1.5, py: 0.5, bgcolor: tokens.status.successBg, borderBottom: "1px solid #D1FAE5" }}>
                          <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.bold, color: tokens.status.success }}>
                            Correct Stacking
                          </Typography>
                        </Box>
                        <Box sx={{ p: 1.5 }}>
                          <Stack spacing={0.5}>
                            {generalTariffs.map((t, i) => (
                              <Stack key={`g-${i}`} direction="row" justifyContent="space-between">
                                <Typography sx={{ fontSize: tokens.fontSize.base, color: tokens.text.secondary }}>{t.columnName}</Typography>
                                <Typography sx={{ fontSize: tokens.fontSize.base, fontWeight: tokens.fontWeight.bold, color: tokens.text.body }}>{(t.rate * 100).toFixed(2)}%</Typography>
                              </Stack>
                            ))}
                            {ch99Tariffs.map((t, i) => {
                              const code = extractCode(t.columnName);
                              const inCorrectSet = code ? correctSet.has(code) : false;
                              // Non-metal lines: 232 is excluded (doesn't apply)
                              const excluded232 = isNonMetalSplit && is232Tariff(t.columnName);
                              // Metal lines: 232 always applies (correct for metal portion)
                              const forced232 = isMetalSplit && is232Tariff(t.columnName);
                              const isCorrect = forced232 || (inCorrectSet && !excluded232);
                              const isNew = isCorrect && code ? !filedSet.has(code) : false;
                              return (
                                <Stack key={`c-${i}`} direction="row" justifyContent="space-between" sx={{ opacity: isCorrect ? 1 : 0.35 }}>
                                  <Typography sx={{ fontSize: tokens.fontSize.base, color: isNew ? tokens.status.success : tokens.text.secondary, fontWeight: isNew ? tokens.fontWeight.bold : undefined, ...(!isCorrect && { textDecoration: "line-through" }) }}>
                                    {t.columnName}{isNew ? " +" : ""}{excluded232 ? " (non-metal)" : ""}
                                  </Typography>
                                  <Typography sx={{ fontSize: tokens.fontSize.base, fontWeight: tokens.fontWeight.bold, color: isCorrect ? (isNew ? tokens.status.success : tokens.text.body) : tokens.text.disabled }}>
                                    {(t.rate * 100).toFixed(2)}%
                                  </Typography>
                                </Stack>
                              );
                            })}
                            <Stack direction="row" justifyContent="space-between" sx={{ pt: 1, mt: 0.5, borderTop: `1px solid ${tokens.border.default}` }}>
                              <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.bold, color: tokens.status.success }}>Correct Rate</Typography>
                              <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.bold, color: tokens.status.success }}>{(correctRate * 100).toFixed(2)}%</Typography>
                            </Stack>
                            {correctRate !== filedRate && item.declaredValue && (
                              <Stack direction="row" justifyContent="space-between">
                                <Typography sx={{ fontSize: tokens.fontSize.base, color: correctRate > filedRate ? tokens.status.danger : tokens.status.success }}>
                                  {correctRate > filedRate ? "Underpaid" : "Overpaid"}
                                </Typography>
                                <Typography sx={{ fontSize: tokens.fontSize.base, fontWeight: tokens.fontWeight.bold, color: correctRate > filedRate ? tokens.status.danger : tokens.status.success }}>
                                  {formatCurrencyPrecise(Math.abs((correctRate - filedRate) * item.declaredValue))}
                                </Typography>
                              </Stack>
                            )}
                          </Stack>
                        </Box>
                      </Box>
                    </Stack>
                  );
                })()
              ) : (
                <Typography sx={{ color: tokens.text.muted, fontSize: tokens.fontSize.base }}>No data available</Typography>
              )}
            </Box>
          )}


          {/* Metadata */}
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(110px, 1fr))", gap: 1, mb: 1.5, px: 2 }}>
            {[
              { l: "Entry #", v: item.entryNumber || "N/A" },
              { l: "Entry Date", v: item.entryDate ? new Date(item.entryDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "N/A" },
              { l: item.lineNumbers && item.lineNumbers.length > 1 ? "Lines" : "Line",
                v: item.lineNumbers && item.lineNumbers.length > 0
                  ? item.lineNumbers.join(", ")
                  : item.lineNumber != null ? String(item.lineNumber)
                  : item.entryLine ? item.entryLine.split("-").pop() || "N/A"
                  : "N/A" },
              ...(item.entryLines && item.entryLines.length > 0
                ? [{ l: item.entryLines.length > 1 ? "Entry Lines" : "Entry Line", v: item.entryLines.join(", ") }]
                : item.entryLine ? [{ l: "Entry Line", v: item.entryLine }] : []),
              ...(item.confidenceScore !== undefined ? [{ l: "Confidence", v: `${item.confidenceScore}%` }] : []),
              { l: "Audit Status", v: item.auditStatus === "APPROVED" ? "Approved" : item.auditStatus === "NEEDS_REVIEW" ? "Needs Review" : "Discrepancies Found" },
              { l: "Audited", v: item.auditDate ? new Date(item.auditDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "N/A" },
              ...(item.auditUpdatedAt ? [{ l: "Updated", v: new Date(item.auditUpdatedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) }] : []),
            ].map((meta) => (
              <Box key={meta.l}>
                <Typography sx={hdr}>{meta.l}</Typography>
                <Typography sx={{ ...val, mt: 0.25 }}>{meta.v}</Typography>
              </Box>
            ))}
          </Box>

          {/* Description */}
          {item.description && (
            <Box sx={{ mb: 1, px: 2 }}>
              <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.muted, fontWeight: tokens.fontWeight.normal, maxWidth: 600 }}>
                {item.description}
              </Typography>
            </Box>
          )}

          {/* Recommendation */}
          {item.recommendation && (
            <Box sx={{ mx: 2, p: 1, bgcolor: tokens.status.successBg, borderRadius: tokens.radius.md, mb: 1.5 }}>
              <Typography sx={{ ...hdr, color: "#065F46", fontWeight: tokens.fontWeight.bold, mb: 0.5 }}>Recommendation</Typography>
              <Typography sx={{ ...val, color: tokens.text.excerpt }}>{item.recommendation}</Typography>
            </Box>
          )}

          {/* Citations */}
          {item.citations && item.citations.length > 0 && (
            <Box sx={{ mb: 1.5, px: 2 }}>
              <Typography sx={{ ...hdr, mb: 0.5 }}>Sources</Typography>
              {item.citations.map((citation, cidx) => (
                <Box key={cidx} sx={{ p: 1, mb: 0.5, bgcolor: tokens.bg.hover, borderRadius: tokens.radius.sm, borderLeft: `3px solid ${tokens.brand.primary}` }}>
                  <Box sx={{ display: "flex", gap: 1, mb: 0.5 }}>
                    <Chip label={citation.documentName || "Document"} size="small" sx={{ height: 18, fontSize: tokens.fontSize["2xs"], fontWeight: tokens.fontWeight.book, borderRadius: 0.5 }} />
                    {citation.documentType && <Chip label={citation.documentType} size="small" variant="outlined" sx={{ height: 18, fontSize: tokens.fontSize["2xs"], fontWeight: tokens.fontWeight.book, borderRadius: 0.5 }} />}
                    {citation.tradeDataLine && <Chip label={`Line: ${citation.tradeDataLine}`} size="small" sx={{ height: 18, fontSize: tokens.fontSize["2xs"], fontWeight: tokens.fontWeight.book, borderRadius: 0.5, bgcolor: "#E0E7FF", color: "#3730A3" }} />}
                  </Box>
                  {citation.excerpt && <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.secondary, fontStyle: "italic", mt: 0.5 }}>&ldquo;{citation.excerpt}&rdquo;</Typography>}
                </Box>
              ))}
            </Box>
          )}

          {/* Load Full Audit */}
          <Box sx={{ mb: 1.5, px: 2 }}>
            {!fullAudit && !auditLoading && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<LoadIcon sx={{ fontSize: 14 }} />}
                onClick={handleLoadAudit}
                sx={{ fontWeight: tokens.fontWeight.bold, fontSize: tokens.fontSize.xs, borderRadius: tokens.radius.md, px: 2, py: 0.5, color: tokens.text.body, borderColor: tokens.border.default, "&:hover": { borderColor: tokens.text.disabled, bgcolor: tokens.bg.hover } }}
              >
                Load Full Audit
              </Button>
            )}
            {auditLoading && (
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <CircularProgress size={18} />
                <Typography sx={{ fontSize: tokens.fontSize.sm2, color: tokens.text.secondary }}>Loading audit details...</Typography>
              </Stack>
            )}
            {auditError && <Typography sx={{ fontSize: tokens.fontSize.sm2, color: tokens.status.danger }}>{auditError}</Typography>}

            {fullAudit && (
              <Box>
                <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.bold, color: tokens.text.primary, mb: 2 }}>Full Audit Details</Typography>

                {fullAudit.discrepancies && fullAudit.discrepancies.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography sx={{ ...hdr, mb: 1 }}>Discrepancies ({fullAudit.discrepancies.length})</Typography>
                    {fullAudit.discrepancies.map((discrepancy, idx) => (
                      <Accordion key={discrepancy.id || idx} defaultExpanded={idx === 0} sx={{ border: `1px solid ${tokens.border.default}`, borderRadius: `${tokens.radius.lg} !important`, mb: 1, "&:before": { display: "none" }, boxShadow: "none" }}>
                        <AccordionSummary expandIcon={<ExpandMore />} sx={{ bgcolor: `${getSeverityColor(discrepancy.severity)}08`, borderRadius: tokens.radius.lg }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, width: "100%" }}>
                            <SeverityDot severity={discrepancy.severity?.toLowerCase() as "high" | "medium" | "low"} showLabel={false} size={16} />
                            <Chip label={getCategoryLabel(discrepancy.category)} size="small" variant="outlined" sx={{ height: 18, fontSize: tokens.fontSize["2xs"], fontWeight: tokens.fontWeight.book, borderRadius: 0.5 }} />
                            <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.medium, flex: 1, color: tokens.text.body }}>{discrepancy.title}</Typography>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ pt: 2 }}>
                          <Typography sx={{ ...val, mb: 2 }}>{discrepancy.description}</Typography>
                          {discrepancy.citations && discrepancy.citations.length > 0 && (
                            <Box sx={{ mb: 2 }}>
                              <Typography sx={{ ...hdr, mb: 1 }}>Sources</Typography>
                              {discrepancy.citations.map((citation, cidx) => (
                                <Box key={cidx} sx={{ p: 1.5, mb: 1, bgcolor: tokens.bg.hover, borderRadius: tokens.radius.sm, borderLeft: `3px solid ${tokens.brand.primary}` }}>
                                  <Box sx={{ display: "flex", gap: 1, mb: 0.5 }}>
                                    <Chip label={citation.documentName || "Document"} size="small" sx={{ height: 18, fontSize: tokens.fontSize["2xs"], fontWeight: tokens.fontWeight.book, borderRadius: 0.5 }} />
                                    {citation.documentType && <Chip label={citation.documentType} size="small" variant="outlined" sx={{ height: 18, fontSize: tokens.fontSize["2xs"], fontWeight: tokens.fontWeight.book, borderRadius: 0.5 }} />}
                                    {citation.tradeDataLine && <Chip label={`Line: ${citation.tradeDataLine}`} size="small" sx={{ height: 18, fontSize: tokens.fontSize["2xs"], fontWeight: tokens.fontWeight.book, borderRadius: 0.5, bgcolor: "#E0E7FF", color: "#3730A3" }} />}
                                  </Box>
                                  {citation.excerpt && <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.secondary, fontStyle: "italic", mt: 0.5 }}>&ldquo;{citation.excerpt}&rdquo;</Typography>}
                                </Box>
                              ))}
                            </Box>
                          )}
                          {discrepancy.recommendation && (
                            <Box sx={{ p: 1.5, bgcolor: tokens.status.successBg, borderRadius: tokens.radius.sm, border: "1px solid #A7F3D0" }}>
                              <Typography sx={{ ...hdr, color: "#065F46", fontWeight: tokens.fontWeight.bold }}>Recommendation</Typography>
                              <Typography sx={{ ...val, color: tokens.text.excerpt, mt: 0.5 }}>{discrepancy.recommendation}</Typography>
                            </Box>
                          )}
                        </AccordionDetails>
                      </Accordion>
                    ))}
                  </Box>
                )}

                {fullAudit.analysisText && (
                  <Accordion sx={{ border: `1px solid ${tokens.border.default}`, borderRadius: `${tokens.radius.lg} !important`, mb: 1, "&:before": { display: "none" }, boxShadow: "none" }}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.bold, color: tokens.text.body }}>Full Analysis</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography sx={{ ...val, whiteSpace: "pre-wrap", lineHeight: 1.7 }}>{fullAudit.analysisText}</Typography>
                    </AccordionDetails>
                  </Accordion>
                )}

                {fullAudit.thinkingTrail && fullAudit.thinkingTrail.length > 0 && (
                  <Accordion sx={{ border: `1px solid ${tokens.border.default}`, borderRadius: `${tokens.radius.lg} !important`, "&:before": { display: "none" }, boxShadow: "none" }}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.bold, color: tokens.text.body }}>Reasoning Steps ({fullAudit.thinkingTrail.length})</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box component="ol" sx={{ pl: 2, m: 0 }}>
                        {fullAudit.thinkingTrail.map((step, idx) => (
                          <Box component="li" key={idx} sx={{ mb: 1, color: tokens.text.secondary, fontSize: tokens.fontSize.xs }}>{step}</Box>
                        ))}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                )}
              </Box>
            )}
          </Box>

          <Box sx={{ borderTop: `1px solid ${tokens.border.light}` }} />

          {/* Review Controls */}
          <Box sx={{ px: 2, py: 1.5 }}>
            <DiscrepancyReviewControls
              review={localReview}
              onStatusChange={handleStatusChange}
              onAddComment={handleAddComment}
              onRemoveComment={handleRemoveComment}
              canRemoveComment={canRemoveComment}
              isUpdating={isReviewUpdating}
            />
          </Box>
        </Box>

        {/* Footer */}
        <Box sx={{ px: 2, py: 1, borderTop: `1px solid ${tokens.border.default}`, display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="outlined"
            size="small"
            onClick={handleClose}
            sx={{ fontWeight: tokens.fontWeight.semibold, fontSize: tokens.fontSize.xs, borderRadius: tokens.radius.md, px: 2, py: 0.5, color: tokens.text.body, borderColor: tokens.border.default, "&:hover": { borderColor: tokens.text.disabled, bgcolor: tokens.bg.hover } }}
          >
            Close
          </Button>
        </Box>
      </Box>
      ) : null}
    </Drawer>
  );
};

export default ClassificationDetailDialog;
